import { createUseStyles } from 'react-jss';
import { useAppState } from '../state/StateContext';
import moment from 'moment-timezone';
import { DATE_FORMAT } from '../config';
import {
  __,
  formatPrice,
  getAppUrl,
  getLanguage,
  isFullAppUrl,
  withLanguage,
} from '../functions/commom';
import { ReactComponent as RightIcon } from '../assets/icons/right.svg';
import InsuranceListLoadingMock from './InsuranceListLoadingMock';
import { InsuranceQuote } from '../types/insurance.types';
import AnimatedNumber from './AnimatedNumber';
import React from 'react';

const useStyles = createUseStyles({
  planList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    '& .plan-item': {
      display: 'flex',
      backgroundColor: '#ffffff',
      borderRadius: '8px',
      cursor: 'pointer',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden',
      transition: 'all 0.2s ease-in-out',
      justifyContent: 'space-between',
      '& .info': {
        padding: '15px',
        width: '100%',
        maxWidth: '250px',
        color: '#666666',
        fontWeight: '500',
        '& .title': {
          fontSize: '22px',
          fontWeight: 'bold',
          marginBottom: '10px',
          color: '#000000',
        },
      },
      '& .apply_button': {
        backgroundColor: 'var(--dai-primary-color)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '10px',
        color: '#ffffff',
        gap: '10px',
        '& .apply_text': {
          display: 'none',
          fontSize: '16px',
          fontWeight: 'bold',
        },
        '& svg': {
          fill: '#ffffff',
          width: '20px',
          height: '20px',
        },
      },
      '& .price': {
        padding: '10px',
        fontSize: '24px',
        fontWeight: 'bold',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--dai-primary-color)',
        '& .priceWithSymbol': {
          display: 'flex',
          alignItems: 'end',
          gap: '3px',
        },
        '& .symbol': {
          marginBottom: '4px',
          fontSize: '12px',
          color: '#333333',
          fontWeight: 'normal',
        },
        '& .discounted': {
          textDecoration: 'line-through',
          fontSize: '16px',
          color: '#666666',
          fontWeight: 'normal',
        },
        '& .discountPercentage': {
          fontSize: '12px',
          color: '#666666',
          fontWeight: 'bold',
        },
      },
      '&:hover': {
        color: '#ffffff',
        backgroundColor: 'var(--dai-primary-color)',
        '& .info': {
          color: '#ffffff',
          '& .title': {
            color: '#ffffff',
            borderColor: '#ffffff !important',
          },
        },
        '& .price': {
          color: '#ffffff',
          '& .symbol': {
            color: '#ffffff',
          },
          '& .discounted': {
            color: '#ffffff',
          },
          '& .discountPercentage': {
            color: '#ffffff',
          },
        },
      },
    },
  },
  planType: {
    fontSize: '14px',
  },
  planHighLight: {
    fontSize: '14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  planHighLightItem: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  noteContent: {
    fontSize: '14px',
    lineHeight: '1.6',
    maxHeight: '60vh', // Limit height for scrolling
    overflowY: 'auto',
    paddingRight: '10px', // Prevent scrollbar from overlapping text
    '& p': {
      marginBottom: '15px',
    },
    '& ul': {
      paddingLeft: '20px',
      marginBottom: '15px',
    },
    '& li': {
      marginBottom: '8px',
    },
    '& h4': {
      fontSize: '16px',
      fontWeight: 'bold',
      marginTop: '20px',
      marginBottom: '10px',
    },
  },
  '@media (max-width: 768px)': {
    planList: {
      '& .plan-item': {
        flexDirection: 'column',
        '& .info': {
          maxWidth: '100%',
          borderBottom: '1px solid #ebebeb',
        },
        '& .apply_button': {
          padding: '15px',
          '& svg': {
            width: '14px',
            height: '14px',
          },
          '& .apply_text': {
            display: 'block',
          },
        },
        '& .price': {
          flexDirection: 'row',
          gap: '5px',
          alignItems: 'end',
          '& .discounted': {
            marginBottom: '2px',
          },
        },
      },
    },
  },
});

const getChineseDiscount = (payPercentage: number) => {
  const map = ['零', '一', '二', '三', '四', '五', '六', '七', '八', '九'];
  const tens = Math.floor(payPercentage / 10);
  const units = payPercentage % 10;

  // Safety check for unexpected values
  if (tens < 1 || tens > 9) return payPercentage + '%';

  let result = map[tens];
  if (units !== 0) {
    result += map[units];
  }
  return result + '折';
};

function InsuranceTierList({
  quoteList,
}: {
  quoteList: InsuranceQuote | null;
}) {
  const classes = useStyles();
  const state = useAppState();

  const lang = getLanguage();

  const category = state?.searchForm?.category?.get() || 'travel';
  const id = state?.searchForm?.id?.get();

  const insurancePlans = state?.insurancePlans?.get();

  const selectedInsurance = Array.isArray(insurancePlans)
    ? insurancePlans.find((plan) => plan.id === id)
    : undefined;

  // Helper function to filter out undefined values
  const filterUndefinedValues = (
    obj: Record<string, any>
  ): Record<string, string> => {
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([_, value]) => value !== undefined)
        .map(([key, value]) => [key, String(value)])
    );
  };

  const handleTierClick = (e: React.MouseEvent, url: string) => {
    e.preventDefault();
    window.location.href = url;
  };

  return (
    <>
      <div className={classes.planList}>
        {quoteList && quoteList.quotes && quoteList.quotes.length > 0
          ? quoteList.quotes.map((tier, index: number) => {
              // For generating url params
              const TravelParams = {
                departureDate: moment(
                  state?.searchForm?.departureDate?.get()
                ).format(DATE_FORMAT),
                returnDate: selectedInsurance?.isAnnualPlan
                  ? moment(state?.searchForm?.departureDate?.get())
                      .add(364, 'day')
                      .format(DATE_FORMAT)
                  : moment(state?.searchForm?.returnDate?.get()).format(
                      DATE_FORMAT
                    ),
                coverageType:
                  state?.searchForm?.coverageType?.get() || 'individual',
                withCruise: state?.searchForm?.withCruise?.get()
                  ? 'true'
                  : undefined,
                couponCode: state?.searchForm?.couponCode?.get() || undefined,
              };

              // For home insurance
              const HomeParams = {
                departureDate: moment(
                  state?.searchForm?.departureDate?.get()
                ).format(DATE_FORMAT),
                coverageType:
                  state?.searchForm?.coverageType?.get() || 'individual',
                floorArea: (
                  state?.searchForm?.floorArea?.get() || ''
                ).toString(),
                areaType: state?.searchForm?.areaType?.get() || 'net',
                buildingAge: (
                  state?.searchForm?.buildingAge?.get() || ''
                ).toString(),
                couponCode: state?.searchForm?.couponCode?.get() || undefined,
              };

              let params = category === 'travel' ? TravelParams : HomeParams;

              // Filter out any undefined values and convert all values to strings
              const filteredParams = filterUndefinedValues(params);

              const redirectUrl = withLanguage(
                !isFullAppUrl()
                  ? `${getAppUrl()}/insurance/${category}/${id}/${
                      tier.id
                    }?${new URLSearchParams(filteredParams).toString()}`
                  : `/insurance/${category}/${id}/${
                      tier.id
                    }?${new URLSearchParams(filteredParams).toString()}`
              );

              const discountPercentage =
                tier.premium && tier.discount
                  ? ((tier.discount / tier.premium) * 100).toFixed(0)
                  : 0;

              return (
                <div
                  key={index}
                  className="plan-item"
                  onClick={(e) => handleTierClick(e, redirectUrl)}
                >
                  <div className="info">
                    {/* Only show tier name for single_trip plan, otherwise show plan name */}
                    <div className="title">
                      {id === 'single_trip'
                        ? __(quoteList.name || '') + ' - ' + __(tier.name || '')
                        : __(quoteList.name || '')}
                    </div>
                    <div className={classes.planHighLight}>
                      {tier.highlightItems?.map((highlight) => (
                        <div
                          key={highlight.id}
                          className={classes.planHighLightItem}
                        >
                          <div>{__(highlight.name)}</div>
                          <div>{formatPrice(highlight.value, '$')}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="price">
                    <div className="priceWithSymbol">
                      <AnimatedNumber
                        value={tier.discount ? tier?.discount : tier.premium}
                        format="currency"
                        prefix="$"
                      />
                    </div>

                    {tier?.discount && tier.premium && tier.discount < tier.premium ? (
                      <>
                        <div className="discounted">
                          {formatPrice(tier.premium || 0, '$')}
                        </div>

                        <div className="discountPercentage">
                          {lang === 'tc'
                            ? getChineseDiscount(Number(discountPercentage))
                            : 100 - Number(discountPercentage) + __('% OFF')}
                        </div>
                      </>
                    ) : null}
                  </div>
                  <div className="apply_button">
                    <div className="apply_text">{__('Apply Now')}</div>
                    <RightIcon />
                  </div>
                </div>
              );
            })
          : Array.from({ length: 3 }).map((_, index) => (
              <InsuranceListLoadingMock key={index} height="100px" />
            ))}
      </div>
    </>
  );
}

export default InsuranceTierList;
